import parse from './lib/parser.js'
import * as d3 from 'd3-selection'
import { EventEmitter } from 'events'

const LOCKED = 'cleaver-code-match-block-locked'
const MATCHED = 'cleaver-code-match-block'

class Cleaver extends EventEmitter {

  constructor () {
    super()
    const styles = window.getComputedStyle(document.body)
    const linePadding = parseInt(styles.getPropertyValue('--cleaver-code-line-padding'))
    const lineHeight = parseInt(styles.getPropertyValue('--cleaver-code-line-height'))

    this.dimensions = {
      lineHeight: linePadding * 2 + lineHeight,
      containerPadding: parseInt(styles.getPropertyValue('--cleaver-padding'))
    }
  }

  /*
   * Receives a DOM container element and a data object.
   * Displays the formatted data in the container and allows
   * the user to highlight repeated paths within the object.
   */
  render (container, data) {
    if (!container) {
      throw new Error('container is required')
    }

    let el = d3.select(container)
               .selectAll('.cleaver')
               .data([data])

    this._el = el.enter()
                 .append('div')
                 .attr('class', 'cleaver')
                 .merge(el)
                 .on('mouseout', this._clearUnlockedMatches.bind(this))

    this._el
        .selectAll('.cleaver-code-line')
        .data(parse)
        .enter()
        .append('div')
          .attr('class', 'cleaver-code-line')
          .html(d => d.html)
          .select(function (d) {
            // TODO This is an expensive operation so we can figure out how wide to make matching code blocks
            d._width = this.getBoundingClientRect().width
            return this
          })
          .on('mouseover', (e, d) => this._search(d.jsonPath))
          .on('click', this._lock.bind(this))
  }

  /*
   * If there is a set path (i.e. there are locked matched code blocks),
   * clear it. This ignores any temporary matched blocks that are showing
   * from a mouseover event.
   */
  clearPath () {
    // Remove any currently locked elements
    this._el.selectAll(`.${LOCKED}`)
            .remove()
  }

  /*
   * Returns an array of the path used for the current locked matched code
   * blocks. The path can then be constructed/escaped as necessary into a
   * JSONPath string.
   */
  getPath () {
    let data = this._el.selectAll(`.${LOCKED}`).data()
    if (data?.length) {
      return data[0].path
    } else {
      return null
    }
  }

  /*
   * Receives an array of property names used to build a locked matched
   * block.
   */
  setPath (path) {
    if (!Array.isArray(path)) {
      return // no-op
    }

    this._search(path.join('.'))
    this._lock()
  }

  /*
   * Clears matched code blocks if they exist. Ignores the locked blocks
   */
  _clearUnlockedMatches () {
    this._el.selectAll(`.${MATCHED}:not(.${LOCKED})`).remove()
  }

  _lock () {
    // Clear the old locked blocks
    this._el.selectAll(`.${LOCKED}`).remove()

    // Mark current match-block elements w/ locked
    this._el.selectAll(`.${MATCHED}`)
            .classed(LOCKED, true)

    this.emit('change', this.getPath())
  }

  _search (path) {
    let matches = []
      , height = this.dimensions.lineHeight
      , lines = this._el.selectAll('.cleaver-code-line')
      , locked = this._el.selectAll(`.${LOCKED}`)

    lines.filter(d => {
      if (typeof d.blockStart !== 'number') {
        // Not a start block
        return false
      }

      if (d.matchPath !== path) {
        // Not the same path
        return false
      }

      return true
    })
    .each(match => {
      let top = (match.blockStart * height) + this.dimensions.containerPadding

      if (locked.size()) {
        let lockedTops = locked.data().map(match => match.top)
        if (lockedTops.includes(top)) {
          return // A locked block is already drawn, we don't want to include
                 // this match
        }
      }

      matches.push({
        path: match.path,
        indent: match.depth,
        top,
        height: (match.blockEnd - match.blockStart) * height + height,
        start: match.blockStart,
        end: match.blockEnd
      })
    })

    let nodes = lines.selectAll('.cleaver-code-line-contents').nodes()

    matches.forEach(match => {
      let lines = nodes.slice(match.start, match.end + 1)
      match.width = Math.max(...lines.map(line => line.offsetWidth))
    })

    let indent = matches.length ? matches[0].indent : 0
      , width = Math.max(...matches.map(m => m.width))
      , blocks = this._el.selectAll(`.${MATCHED}:not(.${LOCKED})`)
                         .data(matches, d => d.top) // Key off top location

    blocks.exit().remove()

    blocks.enter()
          .insert('div', ':first-child')
            .attr('class', MATCHED)
            .merge(blocks)
            .style('top', d => `${d.top}px`)
            .style('height', d => `${d.height}px`)
            .style('width', d => `calc(${width}px - ${indent}em`)
            .style('margin-left', indent + 'em' )
  }

}

export default Cleaver
